import { useState, useEffect } from "react";
import { Printer, Ban, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { startOfMonth, endOfMonth, format } from "date-fns";
import Loading from "../../../../components/loading";
import libFloat from "../../../../util/libFloat";
import functions from "../../../../util/functions";
import LibDateTime from "../../../../util/libDateTime";
import Notification from "../../../../components/toast";
import MessageDialog from "../../../../components/ui/dialog/MessageDialog";
import reqBoleto from "../../../../services/reqCobranca/reqBoleto";
import { generateReport } from "../../../../util/reportRegister";
import { saveAs } from "file-saver";
import { bytesDeBase64 } from "../hooks";
import { Campo } from "./filtro-busca";

export default function TabelaRemessasEmitidas() {
	const [dtIni, setDtIni] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
	const [dtFim, setDtFim] = useState(format(endOfMonth(new Date()), "yyyy-MM-dd"));
	const [remessas, setRemessas] = useState([]);
	const [carregando, setCarregando] = useState(false);
	const [imprimindoId, setImprimindoId] = useState(null);
	const [cancelandoId, setCancelandoId] = useState(null);
	// A remessa que espera confirmacao no dialogo.
	const [remessaCancelar, setRemessaCancelar] = useState(null);
	const [erro, setErro] = useState("");

	async function carregarRemessas() {
		setCarregando(true);
		setErro("");
		try {
			const res = await reqBoleto.listarRemessas(
				functions.dateByString(dtIni),
				functions.dateByString(dtFim)
			);
			const lista = Array.isArray(res?.data) ? res.data : [];
			setRemessas(lista);
		} catch (e) {
			setErro(e?.response?.data?.error || e?.message || "Falha ao buscar histórico de remessas.");
			setRemessas([]);
		} finally {
			setCarregando(false);
		}
	}

	useEffect(() => {
		carregarRemessas();
	}, []);

	// As rotas devolvem a recusa como texto puro.
	function msgErro(e, padrao) {
		if (typeof e?.response?.data === "string") return e.response.data;

		return e?.response?.data?.error || e?.message || padrao;
	}

	async function handleCancelar() {
		const item = remessaCancelar;
		setRemessaCancelar(null);
		if (!item) return;

		const chave = `${item.id_boleto_config}-${item.numero_remessa}`;
		setCancelandoId(chave);
		try {
			const response = await reqBoleto.cancelarRemessa(item.id_boleto_config, item.numero_remessa);
			Notification.showToast({
				message: `Remessa cancelada. ${response.data?.titulos_cancelados ?? 0} parcela(s) liberada(s).`,
				type: "success",
			});
			await carregarRemessas();
		} catch (e) {
			Notification.showToast({ message: msgErro(e, "Erro ao cancelar a remessa."), type: "error" });
		} finally {
			setCancelandoId(null);
		}
	}

	async function handleImprimir(item) {
		const chave = `${item.id_boleto_config}-${item.numero_remessa}`;
		setImprimindoId(chave);
		try {
			const response = await reqBoleto.imprimirRemessa(item.id_boleto_config, item.numero_remessa);
			const dados = response.data;
			const nome = dados.nome_arquivo || `boletos-${item.numero_remessa}.pdf`;
			const blob = new Blob([bytesDeBase64(dados.arquivo)], { type: "application/pdf" });

			// Mesma saída dos relatórios: abre em aba nova, e quem imprime
			// é o visualizador do navegador. Se o popup for bloqueado, ao
			// menos o arquivo chega.
			const aba = window.open(URL.createObjectURL(blob), "_blank");
			if (!aba) saveAs(blob, nome);
		} catch (e) {
			Notification.showToast({ message: msgErro(e, "Erro ao gerar o PDF dos boletos."), type: "error" });
		} finally {
			setImprimindoId(null);
		}
	}

	function handleExportar(tipo) {
		if (remessas.length === 0) {
			Notification.showToast({ message: "Nenhuma remessa para exportar.", type: "warning" });
			return;
		}

		const columns = [
			{ key: "numero_remessa", label: "Nº Remessa", align: "center" },
			{ key: "convenio", label: "Convênio" },
			{ key: "data_remessa_fmt", label: "Data Emissão" },
			{ key: "qtde_titulos", label: "Qtd. Títulos", align: "right" },
			{ key: "valor_total", label: "Valor Total", format: "currency", align: "right" },
		];

		const formattedData = remessas.map((item) => ({
			...item,
			data_remessa_fmt: item.data_remessa
				? LibDateTime.parseDelphiLocal(item.data_remessa, "dd/MM/yyyy HH:mm")
				: "-",
		}));

		const totalQtd = remessas.reduce((acc, curr) => acc + Number(curr.qtde_titulos || 0), 0);
		const totalVal = remessas.reduce((acc, curr) => acc + Number(curr.valor_total || 0), 0);

		generateReport({
			data: formattedData,
			columns,
			format: tipo,
			fileName: "Relatório de Remessas Emitidas",
			headerSubtitle: "Histórico de Arquivos de Remessa Gerados",
			filters: {
				dtIni: { label: "Data Inicial", value: format(new Date(dtIni + "T00:00:00"), "dd/MM/yyyy") },
				dtFim: { label: "Data Final", value: format(new Date(dtFim + "T00:00:00"), "dd/MM/yyyy") },
			},
			totals: {
				qtd: { label: "Total Títulos", value: totalQtd },
				valor: { label: "Valor Total Remessas", value: totalVal, format: "currency" },
			},
		});
	}

	return (
		<div className="flex-1 flex flex-col min-h-0 gap-4">
			{/* Mesmo desenho do filtro da aba de busca, para a barra nao
			    dancar quando o usuario troca de aba. */}
			<div className="shrink-0 bg-white border border-gray-300 rounded-md p-4 flex flex-wrap items-end gap-4">
				<Campo label="Emitida de">
					<input
						type="date"
						value={dtIni}
						onChange={(e) => setDtIni(e.target.value)}
						className="border border-gray-300 rounded px-3 py-2 text-sm"
					/>
				</Campo>

				<Campo label="até">
					<input
						type="date"
						value={dtFim}
						onChange={(e) => setDtFim(e.target.value)}
						className="border border-gray-300 rounded px-3 py-2 text-sm"
					/>
				</Campo>

				<button
					onClick={carregarRemessas}
					disabled={carregando}
					className="px-4 py-2 rounded-md text-sm font-medium bg-customBlue-primary text-white hover:bg-customBlue-secondary disabled:bg-gray-300 disabled:text-gray-500"
				>
					{carregando ? "Buscando..." : "Buscar"}
				</button>

				<div className="ml-auto flex items-center gap-2">
					<button
						type="button"
						onClick={() => handleExportar("pdf")}
						disabled={remessas.length === 0}
						className="btn btn-sm btn-outline-danger inline-flex items-center gap-1.5"
					>
						<FileText size={14} /> PDF
					</button>
					<button
						type="button"
						onClick={() => handleExportar("excel")}
						disabled={remessas.length === 0}
						className="btn btn-sm btn-outline-success inline-flex items-center gap-1.5"
					>
						<FileSpreadsheet size={14} /> Excel
					</button>
				</div>
			</div>

			{erro && (
				<div className="shrink-0 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3">
					{erro}
				</div>
			)}

			<div className="flex-1 flex flex-col min-h-0 border rounded-md bg-white border-gray-300 overflow-hidden">
				<div className="flex-1 overflow-y-auto">
					<table className="table table-portal mb-0 w-full border-b">
						<thead className="color-table sticky top-0 z-10 shadow-sm">
							<tr>
								<th className="text-center font-semibold py-2 border-b w-28">
									<span className="text-color-table">Nº Remessa</span>
								</th>
								<th className="text-left font-semibold py-2 border-b">
									<span className="text-color-table">Convênio</span>
								</th>
								<th className="text-center font-semibold py-2 border-b w-44">
									<span className="text-color-table">Data Emissão</span>
								</th>
								<th className="text-right font-semibold py-2 border-b w-28">
									<span className="text-color-table">Qtd. Títulos</span>
								</th>
								<th className="text-right font-semibold py-2 border-b w-32">
									<span className="text-color-table">Valor Total</span>
								</th>
								<th className="text-center font-semibold py-2 border-b w-64">
									<span className="text-color-table">Ação</span>
								</th>
							</tr>
						</thead>

						<tbody>
							{remessas.map((item) => {
								const chave = `${item.id_boleto_config}-${item.numero_remessa}`;
								const imprimindo = imprimindoId === chave;
								const cancelando = cancelandoId === chave;

								return (
									<tr key={chave} className="hover:bg-customBlue-primary/5">
										<td className="text-center align-middle font-semibold text-gray-800 py-2">
											#{String(item.numero_remessa).padStart(5, "0")}
										</td>
										<td className="text-left align-middle font-medium">
											{item.convenio || `Convênio ${item.id_boleto_config}`}
										</td>
										<td className="text-center align-middle">
											{item.data_remessa
												? LibDateTime.parseDelphiLocal(item.data_remessa, "dd/MM/yyyy HH:mm")
												: "-"}
										</td>
										<td className="text-right align-middle text-gray-500 text-sm">
											{item.qtde_titulos}
										</td>
										<td className="text-right align-middle text-emerald-600 font-semibold">
											{libFloat.strToCurrency(item.valor_total || 0)}
										</td>
										<td className="text-center align-middle">
											<div className="flex items-center justify-center gap-2">
												<button
													type="button"
													onClick={() => handleImprimir(item)}
													disabled={imprimindo}
													title="Abrir os boletos desta remessa em PDF"
													className="btn btn-sm btn-outline-primary inline-flex items-center gap-1.5"
												>
													{imprimindo ? (
														<Loader2 size={14} className="animate-spin" />
													) : (
														<Printer size={14} />
													)}
													Imprimir
												</button>

												<button
													type="button"
													onClick={() => setRemessaCancelar(item)}
													disabled={cancelando}
													title="Soltar as parcelas desta remessa para uma remessa nova"
													className="btn btn-sm btn-outline-danger inline-flex items-center gap-1.5"
												>
													{cancelando ? (
														<Loader2 size={14} className="animate-spin" />
													) : (
														<Ban size={14} />
													)}
													Cancelar
												</button>
											</div>
										</td>
									</tr>
								);
							})}

							{remessas.length === 0 && !carregando && (
								<tr>
									<td colSpan={6} className="text-center text-gray-400 py-10">
										Nenhuma remessa encontrada no período.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{carregando && (
					<div className="flex h-44">
						<Loading mensagem="Buscando remessas..." />
					</div>
				)}
			</div>

			<MessageDialog
				open={Boolean(remessaCancelar)}
				variant="warning"
				buttons="confirmCancel"
				autoFocus="cancel"
				title={`Cancelar remessa #${String(remessaCancelar?.numero_remessa ?? "").padStart(5, "0")}`}
				description={
					<div className="flex flex-col gap-2">
						<p className="mb-0">
							As <strong>{remessaCancelar?.qtde_titulos}</strong> parcelas voltam a ficar
							disponíveis para entrar numa remessa nova. O número da remessa e os nossos
							números usados não são reaproveitados.
						</p>
						<p className="mb-0">
							<strong>Só cancele se este arquivo .REM ainda não foi enviado ao banco.</strong>{" "}
							O cancelamento é só aqui — o banco não fica sabendo. Se o título já foi
							registrado lá, a retirada tem que ser pedida por remessa de baixa, senão ficam
							dois boletos vivos para a mesma parcela.
						</p>
					</div>
				}
				onConfirm={handleCancelar}
				onCancel={() => setRemessaCancelar(null)}
			/>
		</div>
	);
}
